import { useMemo, useState } from "react";
import { IChannel, IProductAnalysisWidgetData } from "@ts/common-types";
import {
    DEEPDIVE_METRICS,
    REVIEWS_METRICS,
    MISC_PA_REPORT_WIDGET,
} from "@ts/constants";
import { Progress, Rate, Space, Table } from "antd";
import { useSelector } from "react-redux";
import WidgetCard from "./WidgetCard";
import { roundToDynamicPrecisionForRating } from "@/ts/widget";

interface IProductAnalysisWidget {
    widgetData: Array<IProductAnalysisWidgetData>;
    title: string;
    type: string;
}

const ProductAnalysisWidget: React.FC<IProductAnalysisWidget> = ({
    widgetData,
    title,
    type,
}) => {
    const currencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );
    const [metrics] = useState<string[]>(
        type === "reviews" ? REVIEWS_METRICS : DEEPDIVE_METRICS
    );
    const [dataSource] = useState(
        metrics.map((metric, index) => ({
            key: index,
            metric,
        }))
    );

    const colDefs = useMemo(() => {
        return [
            {
                title: "",
                dataIndex: "metric",
                key: "metric",
                ["fixed" as string]: "left",
                width: 150,
                render: (_: any, record: (typeof dataSource)[0]) => {
                    return <strong>{record.metric}</strong>;
                },
            },
            ...(widgetData
                ? widgetData.map((item) => ({
                      title: (
                          <div
                              style={
                                  item.productImage
                                      ? { textAlign: "center" }
                                      : {}
                              }
                          >
                              {item.productImage && (
                                  <img
                                      src={item.productImage}
                                      alt="Product"
                                      style={{ width: 100, height: 100 }}
                                  />
                              )}

                              <p title={item.productName || ""}>
                                  {item.productName.length > 80
                                      ? item.productName.substring(0, 80) +
                                        "..."
                                      : item.productName}{" "}
                                  {item.skuLink && (
                                      <a
                                          href={item.skuLink}
                                          target="_blank"
                                          className="text-decoration-none"
                                      >
                                          ({item.sku})
                                      </a>
                                  )}
                              </p>
                          </div>
                      ),
                      dataIndex: item.key,
                      key: item.key,
                      width: 200,
                      render: (_: any, record: (typeof dataSource)[0]) => {
                          switch (record.metric) {
                              case "BSR":
                                  return (
                                      <div
                                          className={
                                              item.bsr || item.bsrCategory
                                                  ? "text-center"
                                                  : ""
                                          }
                                      >
                                          <strong>{item.bsr || "-"}</strong>
                                          <p>{item.bsrCategory || "-"}</p>
                                      </div>
                                  );
                              case "Availability %":
                                  return (
                                      <Progress
                                          strokeWidth={25}
                                          strokeLinecap="square"
                                          percent={item.availability}
                                          showInfo={true}
                                          strokeColor="#1890ff"
                                      />
                                  );
                              case "Paid SOV":
                                  return (
                                      <Progress
                                          strokeWidth={25}
                                          strokeLinecap="square"
                                          percent={item.paidSOV}
                                          showInfo={true}
                                          strokeColor="#1890ff"
                                      />
                                  );
                              case "Organic SOV":
                                  return (
                                      <Progress
                                          strokeWidth={25}
                                          strokeLinecap="square"
                                          percent={item.organicSOV}
                                          showInfo={true}
                                          strokeColor="#1890ff"
                                      />
                                  );
                              case "Avg Price":
                                  return item.avgPrice
                                      ? `${currencySymbol}${item.avgPrice}`
                                      : "-";
                              case "Avg Discount":
                                  return (
                                      <Progress
                                          strokeWidth={25}
                                          strokeLinecap="square"
                                          percent={item.avgDiscount}
                                          showInfo={true}
                                          strokeColor="#1890ff"
                                      />
                                  );
                              case "Price Range":
                                  return `${currencySymbol}${item.priceRange}`;
                              case "Discount Range":
                                  return item.discountRange;
                              case "Rating":
                                  return (
                                      <span className="d-flex align-items-center">
                                          <span className="me-2">
                                              {+item.rating || 0}
                                          </span>
                                          <Rate
                                              className="rating-star"
                                              value={
                                                  roundToDynamicPrecisionForRating(
                                                      +item.rating
                                                  ) || 0
                                              }
                                              allowHalf
                                              disabled
                                          />
                                      </span>
                                  );
                              case "#Reviews":
                              case "#Ratings":
                                  return item.ratingsCount ? (
                                      <Progress
                                          strokeWidth={25}
                                          strokeLinecap="square"
                                          percent={
                                              (item.ratingsCount /
                                                  (MISC_PA_REPORT_WIDGET.ratingsCount +
                                                      1)) *
                                              100
                                          }
                                          showInfo={true}
                                          strokeColor="#1890ff"
                                          format={() =>
                                              `${(
                                                  (record.metric === "#Ratings"
                                                      ? item.ratingsCount
                                                      : item.reviewsCount) /
                                                  1000
                                              ).toFixed(1)}K`
                                          }
                                      />
                                  ) : (
                                      "-"
                                  );
                              default:
                                  return null;
                          }
                      },
                  }))
                : []),
        ];
    }, [widgetData]);

    return (
        <WidgetCard className="my-3 w-100">
            <h5 className="mb-3">{title}</h5>
            <Space
                direction="vertical"
                size="middle"
                style={{ display: "flex" }}
            >
                <Table
                    dataSource={dataSource}
                    columns={colDefs}
                    pagination={false}
                    rowClassName={() => "ant-table-row"}
                    style={{ textAlign: "center" }}
                    scroll={{ x: 1300 }}
                />
            </Space>
        </WidgetCard>
    );
};

export default ProductAnalysisWidget;